import { useState, useEffect } from 'react';
import { SectionWrapper } from '../SectionWrapper';
import { Landmark, Plus, Trash2, Check, ChevronsUpDown, Building2, Pencil, Loader2 } from 'lucide-react';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { getCurrentUserCompanyId } from '@/lib/userUtils';
import chaseSymbol from '@/assets/bank-logos/chase-bank-symbol.svg';
import boaSymbol from '@/assets/bank-logos/bank-of-america-symbol.svg';
import wellsFargoSymbol from '@/assets/bank-logos/wells-fargo-symbol.svg';
import capitalOneSymbol from '@/assets/bank-logos/capital-one-symbol.svg';
import citiSymbol from '@/assets/bank-logos/citi-bank-symbol.jpeg';

const US_BANKS = [
  { name: 'JPMorgan Chase', logo: chaseSymbol },
  { name: 'Bank of America', logo: boaSymbol },
  { name: 'Wells Fargo', logo: wellsFargoSymbol },
  { name: 'Citibank', logo: citiSymbol },
  { name: 'U.S. Bank', logo: 'https://logo.clearbit.com/usbank.com' },
  { name: 'PNC Bank', logo: 'https://logo.clearbit.com/pnc.com' },
  { name: 'Capital One', logo: capitalOneSymbol },
  { name: 'TD Bank', logo: 'https://logo.clearbit.com/td.com' },
  { name: 'Truist Bank', logo: 'https://logo.clearbit.com/truist.com' },
  { name: 'Goldman Sachs Bank', logo: 'https://logo.clearbit.com/goldmansachs.com' },
  { name: 'HSBC Bank', logo: 'https://logo.clearbit.com/us.hsbc.com' },
  { name: 'Fifth Third Bank', logo: 'https://logo.clearbit.com/53.com' },
  { name: 'KeyBank', logo: 'https://logo.clearbit.com/key.com' },
  { name: 'Regions Bank', logo: 'https://logo.clearbit.com/regions.com' },
  { name: 'M&T Bank', logo: 'https://logo.clearbit.com/mtb.com' },
  { name: 'Huntington Bank', logo: 'https://logo.clearbit.com/huntington.com' },
  { name: 'Citizens Bank', logo: 'https://logo.clearbit.com/citizensbank.com' },
  { name: 'First Republic Bank', logo: 'https://logo.clearbit.com/firstrepublic.com' },
  { name: 'Ally Bank', logo: 'https://logo.clearbit.com/ally.com' },
  { name: 'Discover Bank', logo: 'https://logo.clearbit.com/discover.com' },
  { name: 'American Express', logo: 'https://logo.clearbit.com/americanexpress.com' },
  { name: 'Charles Schwab Bank', logo: 'https://logo.clearbit.com/schwab.com' },
  { name: 'USAA', logo: 'https://logo.clearbit.com/usaa.com' },
  { name: 'Navy Federal Credit Union', logo: 'https://logo.clearbit.com/navyfederal.org' },
  { name: 'State Farm Bank', logo: 'https://logo.clearbit.com/statefarm.com' },
  { name: 'BB&T (Truist)', logo: 'https://logo.clearbit.com/bbt.com' },
  { name: 'SunTrust (Truist)', logo: 'https://logo.clearbit.com/suntrust.com' },
  { name: 'Comerica Bank', logo: 'https://logo.clearbit.com/comerica.com' },
  { name: 'Zions Bank', logo: 'https://logo.clearbit.com/zionsbank.com' },
  { name: 'First National Bank', logo: 'https://logo.clearbit.com/fnb-online.com' },
  { name: 'BMO Harris Bank', logo: 'https://logo.clearbit.com/bmoharris.com' },
  { name: 'Santander Bank', logo: 'https://logo.clearbit.com/santanderbank.com' },
  { name: 'Bank of the West', logo: 'https://logo.clearbit.com/bankofthewest.com' },
  { name: 'Union Bank', logo: 'https://logo.clearbit.com/unionbank.com' },
  { name: 'East West Bank', logo: 'https://logo.clearbit.com/eastwestbank.com' },
  { name: 'Cathay Bank', logo: 'https://logo.clearbit.com/cathaybank.com' },
  { name: 'First Citizens Bank', logo: 'https://logo.clearbit.com/firstcitizens.com' },
  { name: 'Frost Bank', logo: 'https://logo.clearbit.com/frostbank.com' },
  { name: 'Associated Bank', logo: 'https://logo.clearbit.com/associatedbank.com' },
  { name: 'Webster Bank', logo: 'https://logo.clearbit.com/websterbank.com' },
  { name: 'First Horizon Bank', logo: 'https://logo.clearbit.com/firsthorizon.com' },
  { name: 'Synovus Bank', logo: 'https://logo.clearbit.com/synovus.com' },
  { name: 'Old National Bank', logo: 'https://logo.clearbit.com/oldnational.com' },
  { name: 'FirstBank', logo: 'https://logo.clearbit.com/efirstbank.com' },
  { name: 'BancorpSouth Bank', logo: 'https://logo.clearbit.com/bancorpsouth.com' },
  { name: 'First Interstate Bank', logo: 'https://logo.clearbit.com/fib.com' },
  { name: 'Washington Federal', logo: 'https://logo.clearbit.com/wafd.com' },
  { name: 'Fulton Bank', logo: 'https://logo.clearbit.com/fultonbank.com' },
  { name: 'Valley National Bank', logo: 'https://logo.clearbit.com/valleynationalbank.com' },
  { name: 'United Community Bank', logo: 'https://logo.clearbit.com/ucbi.com' },
  { name: 'First Merchants Bank', logo: 'https://logo.clearbit.com/firstmerchants.com' },
  { name: 'Prosperity Bank', logo: 'https://logo.clearbit.com/prosperitybankusa.com' },
  { name: 'First Financial Bank', logo: 'https://logo.clearbit.com/ffb1.com' },
  { name: 'First Hawaiian Bank', logo: 'https://logo.clearbit.com/fhb.com' },
  { name: 'Bank of Hawaii', logo: 'https://logo.clearbit.com/boh.com' },
  { name: 'Central Pacific Bank', logo: 'https://logo.clearbit.com/cpbank.com' },
  { name: 'American Savings Bank', logo: 'https://logo.clearbit.com/asbhawaii.com' },
];

interface BankAccount {
  id?: string;
  account_name: string;
  bank_name: string;
  account_number?: string;
  account_type: string;
}

interface BankComboboxProps {
  value: string;
  onValueChange: (value: string) => void;
  onCustomBank?: (bankName: string) => void;
}

const BankCombobox = ({ value, onValueChange, onCustomBank }: BankComboboxProps) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customBankName, setCustomBankName] = useState('');

  const selectedBank = US_BANKS.find(bank => bank.name === value);
  const filteredBanks = US_BANKS.filter(bank =>
    bank.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelectBank = (bankName: string) => {
    onValueChange(bankName);
    setOpen(false);
    setSearchQuery('');
    setShowCustomInput(false);
  };

  const handleCustomBank = () => {
    if (customBankName.trim()) {
      onValueChange(customBankName.trim());
      if (onCustomBank) {
        onCustomBank(customBankName.trim());
      }
      setOpen(false);
      setCustomBankName('');
      setShowCustomInput(false);
      setSearchQuery('');
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          <div className="flex items-center gap-2">
            {selectedBank && (
              <img
                src={selectedBank.logo}
                alt={selectedBank.name}
                className="h-5 w-5 object-contain"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            )}
            <span>{selectedBank ? selectedBank.name : value || "Select bank..."}</span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command>
          <CommandInput 
            placeholder="Search bank..." 
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            <CommandEmpty>
              <div className="py-4 px-2">
                <p className="text-sm text-muted-foreground mb-2">Bank not found?</p>
                {!showCustomInput ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowCustomInput(true)}
                    className="w-full"
                  >
                    Enter Bank Name Manually
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <Input
                      placeholder="Enter bank name"
                      value={customBankName}
                      onChange={(e) => setCustomBankName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleCustomBank();
                        }
                      }}
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={handleCustomBank}
                        className="flex-1"
                      >
                        Add
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setShowCustomInput(false);
                          setCustomBankName('');
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </CommandEmpty>
            <CommandGroup>
              {filteredBanks.map((bank) => (
                <CommandItem
                  key={bank.name}
                  value={`${bank.name} ${bank.name.toLowerCase()}`}
                  onSelect={() => handleSelectBank(bank.name)}
                >
                  <div className="flex items-center gap-2 flex-1">
                    <img
                      src={bank.logo}
                      alt={bank.name}
                      className="h-5 w-5 object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    <span>{bank.name}</span>
                  </div>
                  <Check
                    className={cn(
                      "ml-2 h-4 w-4",
                      value === bank.name ? "opacity-100" : "opacity-0"
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
            {filteredBanks.length === 0 && !showCustomInput && (
              <div className="py-2 px-2 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCustomInput(true)}
                  className="w-full"
                >
                  Enter Bank Name Manually
                </Button>
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const BankAccountsSection = () => {
  const { completeSection, skipSection, updateSectionData } = useOnboarding();
  const { toast } = useToast();
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [existingAccounts, setExistingAccounts] = useState<BankAccount[]>([]);
  const [hasExistingAccounts, setHasExistingAccounts] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<BankAccount | null>(null);
  const [locations, setLocations] = useState<any[]>([]);
  const [accountLocationSettings, setAccountLocationSettings] = useState<Record<string, { appliesToAll: boolean; locationIds: string[] }>>({});
  const [loading, setLoading] = useState(true);
  const [newAccount, setNewAccount] = useState<BankAccount>({
    account_name: '',
    bank_name: '',
    account_number: '',
    account_type: 'checking'
  });

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([loadAccounts(), loadLocations()]);
      setLoading(false);
    };
    loadData();
  }, []);

  const loadLocations = async () => {
    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) return;

      const { data, error } = await supabase
        .from('locations')
        .select('id, location_name, concept_name')
        .eq('company_id', companyId)
        .eq('is_active', true);

      if (error) {
        console.error('Error loading locations:', error);
        return;
      }

      if (data && data.length > 0) {
        setLocations(data);
        
        // Load location settings for existing accounts from database
        const settings: Record<string, { appliesToAll: boolean; locationIds: string[] }> = {};
        
        for (const acc of existingAccounts) {
          if (!acc.id) continue;
          
          // Check if account applies to all locations
          const { data: accountData } = await supabase
            .from('bank_accounts')
            .select('applies_to_all_locations')
            .eq('id', acc.id)
            .single();
          
          const appliesToAll = accountData?.applies_to_all_locations ?? true;
          
          if (appliesToAll) {
            settings[acc.id] = { appliesToAll: true, locationIds: [] };
          } else {
            // Load specific location links
            const { data: locationLinks } = await supabase
              .from('bank_account_locations')
              .select('location_id')
              .eq('bank_account_id', acc.id);
            
            settings[acc.id] = {
              appliesToAll: false,
              locationIds: locationLinks?.map(link => link.location_id) || []
            };
          }
        }
        
        setAccountLocationSettings(settings);
      }
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const loadAccounts = async () => {
    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) return;

      const { data, error } = await supabase
        .from('bank_accounts')
        .select('id, account_name, bank_name, account_type, account_number')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('created_at');

      if (error) {
        console.error('Error loading accounts:', error);
        throw error;
      }
      
      // Map data - account_number is now stored in DB
      if (!data) {
        setExistingAccounts([]);
        setHasExistingAccounts(false);
        return;
      }
      
      const mappedData = data.map((acc: any) => ({
        id: acc.id,
        account_name: acc.account_name,
        bank_name: acc.bank_name,
        account_type: acc.account_type,
        account_number: acc.account_number || '',
      }));
      
      setExistingAccounts(mappedData);
      setHasExistingAccounts(mappedData.length > 0);
    } catch (error) {
      console.error('Error loading accounts:', error);
    }
  };

  const addAccount = () => {
    if (!newAccount.account_name.trim() || !newAccount.bank_name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Account name and bank name are required',
        variant: 'destructive'
      });
      return;
    }

    const accountToAdd = { ...newAccount };
    // Store location settings with the account (temporary until saved)
    if (locations.length > 1 && accountLocationSettings['new']) {
      (accountToAdd as any).locationSettings = accountLocationSettings['new'];
    }

    setAccounts([...accounts, accountToAdd]);
    setNewAccount({
      account_name: '',
      bank_name: '',
      account_number: '',
      account_type: 'checking'
    });
    // Reset location settings for new account
    setAccountLocationSettings(prev => {
      const updated = { ...prev };
      delete updated['new'];
      return updated;
    });
    setShowForm(false);
  };

  const removeAccount = (index: number) => {
    setAccounts(accounts.filter((_, i) => i !== index));
  };

  const openEditModal = (account: BankAccount) => {
    setEditingAccount({ ...account });
    // Load location settings for this account if they exist
    if (locations.length > 1 && !accountLocationSettings[account.id || '']) {
      setAccountLocationSettings(prev => ({
        ...prev,
        [account.id || '']: { appliesToAll: true, locationIds: [] }
      }));
    }
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingAccount(null);
  };

  const saveEditedAccount = async () => {
    if (!editingAccount) return;

    if (!editingAccount.account_name.trim() || !editingAccount.bank_name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Account name and bank name are required',
        variant: 'destructive'
      });
      return;
    }

    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) {
        toast({
          title: 'Error',
          description: 'Company ID not found',
          variant: 'destructive'
        });
        return;
      }

      if (editingAccount.id) {
        // Update existing account in database
        const { error } = await supabase
          .from('bank_accounts')
          .update({
            account_name: editingAccount.account_name,
            bank_name: editingAccount.bank_name,
            account_type: editingAccount.account_type,
            account_number: editingAccount.account_number || null,
            is_active: true,
          })
          .eq('id', editingAccount.id);

        if (error) {
          console.error('Error updating account:', error);
          toast({
            title: 'Error',
            description: 'Failed to update bank account',
            variant: 'destructive'
          });
          return;
        }

        // Update local state
        setExistingAccounts(prev => 
          prev.map(acc => acc.id === editingAccount.id ? editingAccount : acc)
        );

        // Save location settings if multi-location
        if (locations.length > 1 && editingAccount.id && accountLocationSettings[editingAccount.id]) {
          const settings = accountLocationSettings[editingAccount.id];
          const companyId = await getCurrentUserCompanyId();
          
          if (companyId) {
            // Update applies_to_all_locations flag
            await supabase
              .from('bank_accounts')
              .update({ applies_to_all_locations: settings.appliesToAll })
              .eq('id', editingAccount.id);

            // Delete existing location links
            await supabase
              .from('bank_account_locations')
              .delete()
              .eq('bank_account_id', editingAccount.id);

            // Insert new location links if not applying to all
            if (!settings.appliesToAll && settings.locationIds.length > 0) {
              const locationLinks = settings.locationIds.map(locationId => ({
                bank_account_id: editingAccount.id,
                location_id: locationId,
                company_id: companyId,
              }));

              await supabase
                .from('bank_account_locations')
                .insert(locationLinks);
            }
          }
        }

        toast({
          title: 'Success',
          description: 'Bank account updated successfully'
        });
      }

      closeEditModal();
      // Reload accounts to get fresh data
      await loadAccounts();
    } catch (error: any) {
      console.error('Error saving edited account:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save changes',
        variant: 'destructive'
      });
    }
  };

  const handleNext = async () => {
    try {
      const companyId = await getCurrentUserCompanyId();

      if (!companyId) {
        toast({
          title: 'Error',
          description: 'Company ID not found. Please complete Business Profile first.',
          variant: 'destructive'
        });
        return;
      }

      // Check if there are existing accounts in the database
      const { data: existingAccountsInDb } = await supabase
        .from('bank_accounts')
        .select('id')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .limit(1);

      const hasExistingAccounts = existingAccountsInDb && existingAccountsInDb.length > 0;

      // Allow proceeding if there are existing accounts OR if user has added new accounts
      if (accounts.length === 0 && !hasExistingAccounts) {
        toast({
          title: 'No Accounts Added',
          description: 'Please add at least one bank account or skip this section',
          variant: 'destructive'
        });
        return;
      }

      // If there are existing accounts but no new accounts to save, just complete the section
      if (accounts.length === 0 && hasExistingAccounts) {
        console.log('✅ Existing bank accounts found, proceeding without saving new accounts');
        await completeSection('bank_accounts', {
          accounts: existingAccounts,
          totalAccounts: existingAccounts.length,
          hasExistingAccounts: true,
        });
        return;
      }

      console.log('💾 Saving bank accounts:', { companyId, accountCount: accounts.length });

      // Separate accounts into new and existing - prevent duplicates
      const accountsWithId = accounts.filter(a => a.id);
      const newAccounts = accounts.filter(a => !a.id);
      const updatedAccounts = [...accountsWithId];

      // Update existing accounts
      for (const account of accountsWithId) {
        const { error } = await supabase
          .from('bank_accounts')
          .update({
            account_name: account.account_name,
            bank_name: account.bank_name,
            account_type: account.account_type,
            account_number: account.account_number || null,
            is_active: true,
          })
          .eq('id', account.id);

        if (error) {
          console.error('Error updating account:', error);
          throw error;
        }
      }

      // Insert new accounts - check for duplicates first
      if (newAccounts.length > 0) {
        const accountsToInsert = [];
        const accountsToUpdate = [];

        for (const account of newAccounts) {
          // Check if account with same name already exists for this company
          const { data: existingAccount } = await supabase
            .from('bank_accounts')
            .select('id')
            .eq('company_id', companyId)
            .eq('account_name', account.account_name)
            .maybeSingle();

          if (existingAccount) {
            // Update existing instead of inserting
            const { error } = await supabase
              .from('bank_accounts')
              .update({
                account_name: account.account_name,
                bank_name: account.bank_name,
                account_type: account.account_type,
                account_number: account.account_number || null,
                is_active: true,
              })
              .eq('id', existingAccount.id);

            if (error) {
              console.error('Error updating existing account:', error);
              throw error;
            }
            updatedAccounts.push({ ...account, id: existingAccount.id });
          } else {
            accountsToInsert.push({
              account_name: account.account_name,
              bank_name: account.bank_name,
              account_type: account.account_type,
              account_number: account.account_number || null,
              company_id: companyId,
              is_active: true
            });
          }
        }

        // Insert truly new accounts
        if (accountsToInsert.length > 0) {
          console.log('Inserting new accounts:', accountsToInsert);

          const { data: insertedData, error: insertError } = await supabase
            .from('bank_accounts')
            .insert(accountsToInsert)
            .select();

          if (insertError) {
            console.error('Error inserting accounts:', insertError);
            throw insertError;
          }

          console.log('✅ Accounts inserted successfully:', insertedData);
          
          // Update local state with IDs and save location settings
          for (const inserted of insertedData) {
            const originalAccount = newAccounts.find(
              a => a.account_name === inserted.account_name && 
                   a.bank_name === inserted.bank_name &&
                   !a.id
            );
            
            if (originalAccount) {
              const accountWithId = { ...originalAccount, id: inserted.id };
              updatedAccounts.push(accountWithId);
              
              // Save location settings for newly inserted accounts
              const locationSettings = (originalAccount as any).locationSettings;
              if (locations.length > 1 && locationSettings) {
                // Update applies_to_all_locations flag
                await supabase
                  .from('bank_accounts')
                  .update({ applies_to_all_locations: locationSettings.appliesToAll })
                  .eq('id', inserted.id);

                // Insert location links if not applying to all
                if (!locationSettings.appliesToAll && locationSettings.locationIds.length > 0) {
                  const locationLinks = locationSettings.locationIds.map((locationId: string) => ({
                    bank_account_id: inserted.id,
                    location_id: locationId,
                    company_id: companyId,
                  }));

                  await supabase
                    .from('bank_account_locations')
                    .insert(locationLinks);
                }
              }
            }
          }
        }
      }

      // Update local state with IDs to prevent future duplicates
      setAccounts(updatedAccounts);

      // Delete accounts that exist in DB but are not in the current list
      const accountIds = updatedAccounts.filter(a => a.id).map(a => a.id!);
      if (accountIds.length > 0) {
        // Get all existing account IDs for this company
        const { data: allExistingAccounts } = await supabase
          .from('bank_accounts')
          .select('id')
          .eq('company_id', companyId);

        if (allExistingAccounts) {
          const idsToDelete = allExistingAccounts
            .map(acc => acc.id)
            .filter(id => !accountIds.includes(id));

          if (idsToDelete.length > 0) {
            const { error: deleteError } = await supabase
              .from('bank_accounts')
              .delete()
              .in('id', idsToDelete);

            if (deleteError) {
              console.error('Error deleting removed accounts:', deleteError);
              // Don't throw - this is cleanup, not critical
            }
          }
        }
      }

      // Verify the save by reading back
      const { data: verifyData, error: verifyError } = await supabase
        .from('bank_accounts')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true);

      if (verifyError) {
        console.error('Error verifying saved accounts:', verifyError);
      } else {
        console.log('🔍 Verification - Accounts in database:', verifyData);
      }

      toast({
        title: 'Bank Accounts Saved',
        description: `${accounts.length} account(s) saved successfully`
      });

      // Save location settings for new accounts
      for (const account of accounts) {
        if (account.id && locations.length > 1 && accountLocationSettings[account.id]) {
          const settings = accountLocationSettings[account.id];
          
          // Update applies_to_all_locations flag
          await supabase
            .from('bank_accounts')
            .update({ applies_to_all_locations: settings.appliesToAll })
            .eq('id', account.id);

          // Delete existing location links
          await supabase
            .from('bank_account_locations')
            .delete()
            .eq('bank_account_id', account.id);

          // Insert new location links if not applying to all
          if (!settings.appliesToAll && settings.locationIds.length > 0) {
            const locationLinks = settings.locationIds.map(locationId => ({
              bank_account_id: account.id,
              location_id: locationId,
              company_id: companyId,
            }));

            await supabase
              .from('bank_account_locations')
              .insert(locationLinks);
          }
        }
      }

      completeSection('bank_accounts', {
        accounts: accounts.map(a => ({
          account_name: a.account_name,
          bank_name: a.bank_name,
          account_type: a.account_type,
          has_account_number: !!a.account_number,
        })),
        totalAccounts: accounts.length,
      });
    } catch (error: any) {
      console.error('❌ Error saving accounts:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint,
      });
      toast({
        title: 'Error',
        description: error.message || 'Failed to save bank accounts',
        variant: 'destructive'
      });
    }
  };

  const handleSave = async () => {
    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) {
        toast({
          title: 'Error',
          description: 'Company ID not found. Please complete Business Profile first.',
          variant: 'destructive'
        });
        return;
      }

      // Save accounts to database
      for (const account of accounts) {
        if (account.id) {
          // Update existing
          const { error: updateError } = await supabase
            .from('bank_accounts')
            .update({
              account_name: account.account_name,
              bank_name: account.bank_name,
              account_type: account.account_type as any,
              account_number: account.account_number || null,
            })
            .eq('id', account.id);

          if (updateError) {
            console.error('Error updating account:', updateError);
            toast({
              title: 'Error',
              description: `Failed to update account: ${account.account_name}`,
              variant: 'destructive'
            });
            return;
          }
        } else {
          // Insert new
          const { data: insertedAccount, error: insertError } = await supabase
            .from('bank_accounts')
            .insert({
              company_id: companyId,
              account_name: account.account_name,
              bank_name: account.bank_name,
              account_type: account.account_type as any,
              account_number: account.account_number || null,
            })
            .select()
            .single();

          if (insertError || !insertedAccount) {
            console.error('Error inserting account:', insertError);
            toast({
              title: 'Error',
              description: `Failed to save account: ${account.account_name}`,
              variant: 'destructive'
            });
            return;
          }

          // Update local state with ID
          const updatedAccounts = accounts.map(a => 
            a === account ? { ...a, id: insertedAccount.id } : a
          );
          setAccounts(updatedAccounts);
        }
      }

      // Save account location settings
      for (const [accountId, settings] of Object.entries(accountLocationSettings)) {
        // Delete existing links
        await supabase
          .from('bank_account_locations')
          .delete()
          .eq('bank_account_id', accountId);

        // Insert new links if not applies to all
        if (!settings.appliesToAll && settings.locationIds.length > 0) {
          const locationLinks = settings.locationIds.map(locationId => ({
            bank_account_id: accountId,
            location_id: locationId,
          }));

          await supabase
            .from('bank_account_locations')
            .insert(locationLinks);
        }
      }

      // Save to onboarding progress
      await updateSectionData('bank_accounts', {
        accounts: accounts.map(a => ({
          account_name: a.account_name,
          bank_name: a.bank_name,
          account_type: a.account_type,
          has_account_number: !!a.account_number,
        })),
        totalAccounts: accounts.length,
        accountLocationSettings,
      });

      toast({
        title: 'Success',
        description: 'Bank accounts saved successfully',
      });
    } catch (error: any) {
      console.error('Error saving bank accounts:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save bank accounts',
        variant: 'destructive'
      });
    }
  };

  // Show loading state while data is being fetched
  if (loading) {
    return (
      <SectionWrapper
        title="Bank & Credit Card Setup"
        description="Connect your bank accounts and credit cards for automatic transaction tracking and cash flow management."
        icon={<Landmark className="h-6 w-6" />}
        onNext={handleNext}
        onSave={handleSave}
        showSaveButton={true}
        onSkip={() => skipSection('bank_accounts')}
        canSkip
        isNextDisabled={true}
      >
        <div className="space-y-6">
          {/* Integration clarification skeleton */}
          <Skeleton className="h-16 w-full rounded-lg" />
          
          {/* Add Account Button skeleton */}
          <div className="flex justify-center">
            <Skeleton className="h-10 w-40" />
          </div>

          {/* Existing Accounts skeleton */}
          <Card>
            <CardContent className="pt-6">
              <Skeleton className="h-5 w-48 mb-4" />
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div className="flex items-center gap-3 flex-1">
                      <Skeleton className="h-8 w-8 rounded" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-5 w-32" />
                        <Skeleton className="h-4 w-48" />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-8 w-8" />
                      <Skeleton className="h-8 w-8" />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </SectionWrapper>
    );
  }

  return (
    <SectionWrapper
      title="Bank & Credit Card Setup"
      description="Connect your bank accounts and credit cards for automatic transaction tracking and cash flow management."
      icon={<Landmark className="h-6 w-6" />}
      onNext={handleNext}
      onSave={handleSave}
      showSaveButton={true}
      onSkip={() => skipSection('bank_accounts')}
      canSkip
      isNextDisabled={accounts.length === 0 && !hasExistingAccounts}
    >
      <div className="space-y-6">
        {/* Integration clarification */}
        <div className="bg-blue-50 dark:bg-blue-950/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
          <p className="text-sm text-blue-900 dark:text-blue-100">
            <strong>Note:</strong> Account-level bank integrations (for automatic transaction sync) can be configured later in{' '}
            <strong>Settings → Integrations</strong>. Here, you're just adding the bank accounts to your account list.
          </p>
        </div>
        {/* Add Account Button */}
        {!showForm && (
          <div className="flex justify-center">
            <Button onClick={() => setShowForm(true)} className="w-full md:w-auto">
              <Plus className="h-4 w-4 mr-2" />
              Add Bank Account
            </Button>
          </div>
        )}

        {/* Add Account Form */}
        {showForm && (
          <Card>
            <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="account-name">Account Name *</Label>
                <Input
                  id="account-name"
                  placeholder="e.g., Main Checking"
                  value={newAccount.account_name}
                  onChange={(e) => setNewAccount({ ...newAccount, account_name: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bank-name">Bank Name *</Label>
                <BankCombobox
                  value={newAccount.bank_name}
                  onValueChange={(value) => setNewAccount({ ...newAccount, bank_name: value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="account-number">Account Number (Optional)</Label>
                <Input
                  id="account-number"
                  type="text"
                  placeholder="Enter account number"
                  value={newAccount.account_number || ''}
                  onChange={(e) => setNewAccount({ ...newAccount, account_number: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="account-type">Account Type</Label>
                <Select
                  value={newAccount.account_type}
                  onValueChange={(value) => setNewAccount({ ...newAccount, account_type: value })}
                >
                  <SelectTrigger id="account-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="checking">Checking Account</SelectItem>
                    <SelectItem value="savings">Savings Account</SelectItem>
                    <SelectItem value="credit_card">Credit Card</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Multi-location linking - only show if multiple locations exist */}
              {locations.length > 1 && (
                <div className="md:col-span-2 space-y-3 p-4 bg-muted rounded-lg">
                  <Label className="text-sm font-semibold">Location Assignment</Label>
                  <p className="text-xs text-muted-foreground mb-3">
                    Does this bank account apply to all locations or specific ones?
                  </p>
                  <RadioGroup
                    value={accountLocationSettings['new']?.appliesToAll ? 'all' : 'specific'}
                    onValueChange={(value) => {
                      setAccountLocationSettings(prev => ({
                        ...prev,
                        'new': {
                          appliesToAll: value === 'all',
                          locationIds: value === 'all' ? [] : (prev['new']?.locationIds || [])
                        }
                      }));
                    }}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="all" id="new-account-all" />
                      <Label htmlFor="new-account-all" className="cursor-pointer">
                        All locations
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="specific" id="new-account-specific" />
                      <Label htmlFor="new-account-specific" className="cursor-pointer">
                        Specific locations
                      </Label>
                    </div>
                  </RadioGroup>
                  {accountLocationSettings['new'] && !accountLocationSettings['new'].appliesToAll && (
                    <div className="mt-3 space-y-2">
                      {locations.map((location) => (
                        <div key={location.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`new-loc-${location.id}`}
                            checked={accountLocationSettings['new']?.locationIds.includes(location.id)}
                            onCheckedChange={(checked) => {
                              const current = accountLocationSettings['new']?.locationIds || [];
                              const updated = checked
                                ? [...current, location.id]
                                : current.filter(id => id !== location.id);
                              setAccountLocationSettings(prev => ({
                                ...prev,
                                'new': {
                                  appliesToAll: false,
                                  locationIds: updated
                                }
                              }));
                            }}
                          />
                          <Label htmlFor={`new-loc-${location.id}`} className="cursor-pointer text-sm">
                            {location.location_name}
                          </Label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-end gap-2 md:col-span-2">
                <Button onClick={addAccount} className="flex-1">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Account
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowForm(false);
                    setNewAccount({
                      account_name: '',
                      bank_name: '',
                      account_number: '',
                      account_type: 'checking'
                    });
                    // Reset location settings for new account
                    setAccountLocationSettings(prev => {
                      const updated = { ...prev };
                      delete updated['new'];
                      return updated;
                    });
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Info message if existing accounts found */}
        {hasExistingAccounts && accounts.length === 0 && (
          <div className="bg-muted/50 p-4 rounded-lg border border-muted">
            <p className="text-sm text-muted-foreground">
              <strong>Existing accounts found:</strong> You have {existingAccounts.length} bank account(s) already set up. You can proceed to the next step or add more accounts below.
            </p>
          </div>
        )}

        {/* Existing Accounts */}
        {existingAccounts.length > 0 && (
          <Card>
            <CardContent className="pt-6">
              <h3 className="font-semibold text-sm mb-3">Existing Bank Accounts ({existingAccounts.length})</h3>
              <div className="space-y-2">
                {existingAccounts.map((account) => {
                  const bank = US_BANKS.find(b => b.name === account.bank_name);
                  return (
                    <div key={account.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <div className="flex items-center gap-3 flex-1">
                        {bank && (
                          <img
                            src={bank.logo}
                            alt={bank.name}
                            className="h-8 w-8 object-contain rounded"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        )}
                        {!bank && (
                          <div className="h-8 w-8 rounded bg-primary/10 flex items-center justify-center">
                            <Building2 className="h-4 w-4 text-primary" />
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="font-medium">{account.account_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {account.bank_name || 'No bank specified'} - {account.account_type?.replace('_', ' ')}
                          </p>
                          {account.account_number && (
                            <p className="text-xs text-muted-foreground">
                              Account: ••••{account.account_number.slice(-4)}
                            </p>
                          )}
                          {locations.length > 1 && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {accountLocationSettings[account.id || '']?.appliesToAll 
                                ? 'Applies to all locations'
                                : accountLocationSettings[account.id || '']?.locationIds.length 
                                  ? `Applies to ${accountLocationSettings[account.id || ''].locationIds.length} location(s)`
                                  : 'Location assignment not set'}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditModal(account)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={async () => {
                            if (confirm(`Are you sure you want to delete ${account.account_name}?`)) {
                              try {
                                const companyId = await getCurrentUserCompanyId();
                                if (!companyId) return;

                                const { error } = await supabase
                                  .from('bank_accounts')
                                  .update({ is_active: false })
                                  .eq('id', account.id)
                                  .eq('company_id', companyId);

                                if (error) throw error;

                                toast({
                                  title: 'Account Deleted',
                                  description: `${account.account_name} has been archived`,
                                });

                                // Reload accounts
                                loadAccounts();
                              } catch (error: any) {
                                console.error('Error deleting account:', error);
                                toast({
                                  title: 'Error',
                                  description: error.message || 'Failed to delete account',
                                  variant: 'destructive',
                                });
                              }
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Accounts List */}
        {accounts.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-sm">New Accounts to Add ({accounts.length})</h3>
              <p className="text-xs text-muted-foreground">
                These accounts will be saved when you click "Continue"
              </p>
            </div>
            {accounts.map((account, index) => {
              const bank = US_BANKS.find(b => b.name === account.bank_name);
              return (
                <Card key={index}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        {bank && (
                          <img
                            src={bank.logo}
                            alt={bank.name}
                            className="h-10 w-10 object-contain rounded"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        )}
                        {!bank && (
                          <div className="h-10 w-10 rounded bg-primary/10 flex items-center justify-center">
                            <Building2 className="h-5 w-5 text-primary" />
                          </div>
                        )}
                        <div className="space-y-1 flex-1">
                          <h4 className="font-semibold">{account.account_name}</h4>
                          <p className="text-sm text-muted-foreground">{account.bank_name}</p>
                          {account.account_number && (
                            <p className="text-xs text-muted-foreground">
                              Account: ••••{account.account_number.slice(-4)}
                            </p>
                          )}
                          <span className="text-xs text-muted-foreground capitalize">
                            {account.account_type.replace('_', ' ')}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            // Edit: Remove from list and populate form
                            setNewAccount({ ...account });
                            setShowForm(true);
                            removeAccount(index);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeAccount(index)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            <div className="bg-blue-50 dark:bg-blue-950/20 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-blue-900 dark:text-blue-100">
                <strong>Note:</strong> These accounts are pending. They will be saved to your account when you click "Continue" at the bottom of this page.
              </p>
            </div>
          </div>
        )}

        {/* Only show placeholder when there are no accounts at all (no existing and no new) */}
        {accounts.length === 0 && !hasExistingAccounts && (
          <div className="text-center py-8 text-muted-foreground">
            <Landmark className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>No accounts added yet</p>
            <p className="text-sm">Add your first bank account or credit card above</p>
          </div>
        )}
      </div>

      {/* Edit Account Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Bank Account</DialogTitle>
          </DialogHeader>
          {editingAccount && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-account-name">Account Name *</Label>
                <Input
                  id="edit-account-name"
                  placeholder="e.g., Main Checking"
                  value={editingAccount.account_name}
                  onChange={(e) => setEditingAccount({ ...editingAccount, account_name: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-bank-name">Bank Name *</Label>
                <BankCombobox
                  value={editingAccount.bank_name}
                  onValueChange={(value) => setEditingAccount({ ...editingAccount, bank_name: value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-account-number">Account Number (Optional)</Label>
                <Input
                  id="edit-account-number"
                  type="text"
                  placeholder="Enter account number"
                  value={editingAccount.account_number || ''}
                  onChange={(e) => setEditingAccount({ ...editingAccount, account_number: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-account-type">Account Type</Label>
                <Select
                  value={editingAccount.account_type}
                  onValueChange={(value) => setEditingAccount({ ...editingAccount, account_type: value })}
                >
                  <SelectTrigger id="edit-account-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="checking">Checking Account</SelectItem>
                    <SelectItem value="savings">Savings Account</SelectItem>
                    <SelectItem value="credit_card">Credit Card</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Multi-location linking in edit modal */}
              {locations.length > 1 && editingAccount && (
                <div className="md:col-span-2 space-y-3 p-4 bg-muted rounded-lg">
                  <Label className="text-sm font-semibold">Location Assignment</Label>
                  <p className="text-xs text-muted-foreground mb-3">
                    Does this bank account apply to all locations or specific ones?
                  </p>
                  <RadioGroup
                    value={accountLocationSettings[editingAccount.id || '']?.appliesToAll ? 'all' : 'specific'}
                    onValueChange={(value) => {
                      setAccountLocationSettings(prev => ({
                        ...prev,
                        [editingAccount.id || '']: {
                          appliesToAll: value === 'all',
                          locationIds: value === 'all' ? [] : (prev[editingAccount.id || '']?.locationIds || [])
                        }
                      }));
                    }}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="all" id={`edit-${editingAccount.id}-all`} />
                      <Label htmlFor={`edit-${editingAccount.id}-all`} className="cursor-pointer">
                        All locations
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="specific" id={`edit-${editingAccount.id}-specific`} />
                      <Label htmlFor={`edit-${editingAccount.id}-specific`} className="cursor-pointer">
                        Specific locations
                      </Label>
                    </div>
                  </RadioGroup>
                  {accountLocationSettings[editingAccount.id || ''] && !accountLocationSettings[editingAccount.id || ''].appliesToAll && (
                    <div className="mt-3 space-y-2">
                      {locations.map((location) => (
                        <div key={location.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`edit-${editingAccount.id}-loc-${location.id}`}
                            checked={accountLocationSettings[editingAccount.id || '']?.locationIds.includes(location.id)}
                            onCheckedChange={(checked) => {
                              const current = accountLocationSettings[editingAccount.id || '']?.locationIds || [];
                              const updated = checked
                                ? [...current, location.id]
                                : current.filter(id => id !== location.id);
                              setAccountLocationSettings(prev => ({
                                ...prev,
                                [editingAccount.id || '']: {
                                  appliesToAll: false,
                                  locationIds: updated
                                }
                              }));
                            }}
                          />
                          <Label htmlFor={`edit-${editingAccount.id}-loc-${location.id}`} className="cursor-pointer text-sm">
                            {location.location_name}
                          </Label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeEditModal}>
              Cancel
            </Button>
            <Button onClick={saveEditedAccount}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionWrapper>
  );
};
